import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { EuiFlexGroup, EuiFlexItem, EuiCopy } from "@elastic/eui";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import globalFilterTypes from "reduxStore/globalFilter/globalFilter.types";
import { isNullOrUndefined } from "utils";
import Button from "./Button";
import Clients from "services/ClientService/Clients";
import Popover from "components/Popover/Popover";
import Emitter from "services/UtilsService/Emitter";

export const ShareButton = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpen, setOpen] = useState<boolean>(false);
  const [shareLink, setShareLink] = useState<any>(null);

  const { clientId, GlobalTopFilterObject, GlobalTopFilterObjectValues, DatePickerParams } =
    useSelector(globalFilterSelector, shallowEqual);
  const isShareRedirect = location?.search?.includes("share");

  const _params: any = new URLSearchParams(location?.search);
  const _encrypted_params = _params.get("share");

  const updateFilers = (url_config: any) => {
    if (url_config?.date && Object.keys(url_config?.date)?.length > 0) {
      dispatch({
        payload: url_config?.date,
        type: globalFilterTypes.UPDATE_TOP_FILTER_DATES,
      });
    }

    if (url_config?.filters && Object.keys(url_config?.filters)?.length > 0) {
      dispatch({
        payload: { ...url_config?.filters },
        type: globalFilterTypes.UPDATE_TOP_FILTER,
      });
    }

    if (url_config?.values && Object.keys(url_config?.values)?.length > 0) {
      dispatch({
        payload: { ...url_config?.values },
        type: globalFilterTypes.UPDATE_TOP_FILTER_VALUES,
      });
    }

    // if (url_config?.templates && Object.keys(url_config?.templates)?.length > 0) {
    //   dispatch({
    //     payload: { ...url_config?.templates },
    //     type: globalFilterTypes.UPDATE_PERFORMANCE_SERIES,
    //   });
    // }

    setTimeout(() => navigate(location.pathname), 2000);
  };

  const handleClientChange = async (url_config: any) => {
    await Clients.registerCacheHub(url_config?.cid);
    await Clients.loadGlobalClient(url_config?.cid);
  };

  const handleSharedAction = (url_config: any) => {
    if (isNullOrUndefined(url_config?.cid)) return;

    if (clientId !== url_config?.cid) {
      handleClientChange(url_config).then(() => {
        // navigate(location.pathname);
        setTimeout(() => updateFilers(url_config), 2000);
      });
    } else {
      // navigate(location.pathname);
      updateFilers(url_config);
    }
  };

  function safeJsonParse<T = any>(str: string): { value: T | null; error: Error | null } {
    try {
      return { value: JSON.parse(str), error: null };
    } catch (err) {
      return { value: null, error: err as Error };
    }
  }

  useEffect(() => {
    if (!isShareRedirect) return;

    const trimmed = _encrypted_params.slice(1);
    const decode = atob(trimmed);

    const url_config: any = safeJsonParse(decode);
    //console.log("*** url_config --- ", url_config);

    if (url_config?.error) {
      Emitter.emit("displayFailure", "This link appears to be incomplete or wasn’t copied fully.");
    } else {
      //handleSharedAction(url_config?.value); //need to investigate default date
      setTimeout(() => handleSharedAction(url_config?.value), 2000);
    }
  }, [isShareRedirect]);

  useEffect(() => {
    if (!isOpen) return;

    const clearFilters: any = {};
    const clearValues: any = {};

    Object.entries(GlobalTopFilterObject).forEach(([key, val]: any) => {
      if (val?.length > 0 && !isNullOrUndefined(val)) clearFilters[key] = val;
    });
    Object.entries(GlobalTopFilterObjectValues).forEach(([key, val]: any) => {
      if (val?.length > 0 && !isNullOrUndefined(val)) clearValues[key] = val;
    });
    const params = {
      cid: clientId,
      filters: clearFilters,
      values: clearValues,
      date: DatePickerParams,
    };
    const encode = `f${btoa(JSON.stringify(params))}`;
    const _shared_page_url = `${window?.location?.origin}${location?.pathname}?share=${encode}`;
    setShareLink(_shared_page_url);
  }, [isOpen]);

  const onButtonClick = () => {
    setOpen(!isOpen);
  };

  const closePopover = () => setOpen(false);

  const button = (
    <Button
      text="Share"
      type="secondary"
      iconType={"share"}
      onClick={onButtonClick}
      style={{
        background: "var(--card-background)",
        color: "var(--text)",
        border: "1px solid var(--border-color)",
      }}
    />
  );

  return (
    <div className="popover_setup_button_block" data-testid="setupButton">
      <Popover
        button={button}
        isOpen={isOpen}
        onClose={closePopover}
        children={
          <EuiFlexGroup
            direction="column"
            justifyContent={"center"}
            alignItems="center"
            gutterSize="none">
            <EuiFlexItem
              grow={false}
              style={{
                padding: "16px",
                fontWeight: 500,
                wordBreak: "break-all",
                whiteSpace: "normal",
                color: "var(--text)",
              }}>
              Share this View
            </EuiFlexItem>

            <EuiFlexItem
              grow={false}
              style={{
                padding: "8px",
              }}>
              <EuiCopy textToCopy={shareLink || ""} afterMessage="Link copied to clipboard">
                {(copy) => (
                  <Button
                    text="Copy Link"
                    type="primary"
                    iconType={"copy"}
                    onClick={() => {
                      copy();
                    }}
                  />
                )}
              </EuiCopy>
            </EuiFlexItem>
            <EuiFlexItem
              grow={false}
              style={{
                padding: "8px",
              }}>
              <Button
                text="Email Link"
                type="primary"
                iconType={"email"}
                // style={{ background: "#1b8dcd" }}
                onClick={() => {
                  window.location.href = `mailto:?subject=${"WISdom link – quick look when you have a sec"}&body=${shareLink}`;
                }}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
        }
      />
    </div>
  );
};
